import moment from "moment";

/**
 * Column contracts for the COCO bulk payment upload.
 *
 * Rent and SD (security deposit) are separate uploads with separate column
 * sets, so each tab gets its own contract. A contract is the single source of
 * truth for that tab's required-columns card, sample-template download, and
 * row validation — the three can never drift apart.
 *
 * Every column listed must be present as a header in the uploaded file. Only a
 * subset must carry a value on every row: remark / transaction_id / ifsc_code /
 * credited_date / payment_upload_date are legitimately blank for some offline
 * transactions, so they are header-required but value-optional.
 */

// Baseline field-level rules, keyed by column name. A contract may override an
// individual field where the two payment types genuinely differ — see SD's
// payment_month below.
const FIELD_RULES = {
  store_code: { valueRequired: true },
  // `account: true` turns on the digits/length check — the server matches on this
  // field, so a malformed value can never resolve to a landlord.
  landlord_account_no: { valueRequired: true, account: true },
  amount: { valueRequired: true, numeric: true },
  payment_month: { valueRequired: true, month: true },
  transaction_type: { valueRequired: true },
  payment_upload_date: { valueRequired: false, date: true },
  credited_date: { valueRequired: false, date: true },
  remark: { valueRequired: false },
  transaction_id: { valueRequired: false },
  ifsc_code: { valueRequired: false },
};

// Indian bank account numbers run 9–18 digits. Nothing else is accepted because
// a stray space or letter makes the server-side match fail with a reason that
// looks unrelated ("landlord not found").
const ACCOUNT_PATTERN = /^\d{9,18}$/;

const toColumns = (keys, overrides = {}) =>
  keys.map((key) => ({
    key,
    label: key,
    ...(FIELD_RULES[key] || { valueRequired: false }),
    ...(overrides[key] || {}),
  }));

// Both payment types carry the same ten columns, confirmed against the Rent and
// SD bulk-upload payloads. Order is also the order the sample template writes
// its headers in.
const BULK_PAYMENT_COLUMN_KEYS = [
  "store_code",
  "landlord_account_no",
  "amount",
  "payment_month",
  "transaction_type",
  "payment_upload_date",
  "credited_date",
  "remark",
  "transaction_id",
  "ifsc_code",
];

// SD is a one-time security deposit, not a recurring charge, so payment_month
// arrives null — the column is still expected in the file, but a blank value is
// valid. Requiring it (as Rent does) would fail every SD row.
const SD_FIELD_OVERRIDES = {
  payment_month: { valueRequired: false },
};

// Describes what this screen does, not what the server does with the rows. The
// upload applies no matching or comparison of its own — whatever is in the file
// is sent, and whatever the server stores comes back through the history call.
const SHARED_UPLOAD_NOTES = [
  "Rows are uploaded exactly as they appear in your file.",
  "Any row the server rejects is listed below with its reason.",
];

/**
 * Keyed by the tab label rendered in the UI. `slug` is the path segment used by
 * the upload endpoint once the API is wired:
 * POST /coco-store/payments/<slug>/bulk-upload
 */
export const BULK_PAYMENT_CONTRACTS = {
  Rent: {
    slug: "rent",
    templateName: "COCO_Rent_Bulk_Payment",
    columns: toColumns(BULK_PAYMENT_COLUMN_KEYS),
    matchNotes: SHARED_UPLOAD_NOTES,
  },
  SD: {
    slug: "sd",
    templateName: "COCO_SD_Bulk_Payment",
    columns: toColumns(BULK_PAYMENT_COLUMN_KEYS, SD_FIELD_OVERRIDES),
    matchNotes: SHARED_UPLOAD_NOTES,
  },
};

export const BULK_PAYMENT_TABS = Object.keys(BULK_PAYMENT_CONTRACTS);

export const getBulkPaymentContract = (tab) =>
  BULK_PAYMENT_CONTRACTS[tab] || BULK_PAYMENT_CONTRACTS.Rent;

// Excel or JSON — CSV uploads are not accepted for bulk payments.
export const ACCEPTED_FILE_EXTENSIONS = [".xlsx", ".json"];

/**
 * Validates one parsed row against its contract.
 *
 * Every column marked `valueRequired` must carry a value — the flag is per
 * contract, so SD's optional payment_month is honoured. Present values are then
 * checked against whichever format rules the column declares. Blank optional
 * fields pass untouched.
 *
 * Dates must be YYYY-MM-DD and a real calendar date, so 2026-02-30 is rejected
 * rather than silently rolled forward. Amounts must parse to a positive number,
 * because a non-numeric cell is otherwise sent to the server as null.
 *
 * Business rules — whether a store exists, whether a transaction is a duplicate —
 * remain the server's, reported per row in its reply.
 *
 * Returns null when the row is valid, otherwise a reason string.
 */
export function validateBulkPaymentRow(row, columns = []) {
  for (const column of columns) {
    const raw = row?.[column.key];
    const value = raw === null || raw === undefined ? "" : String(raw).trim();

    // Account columns are required regardless of the flag — the server matches on
    // them, so a blank can never resolve to a landlord.
    if ((column.valueRequired || column.account) && !value) {
      return `${column.key} is required`;
    }

    // Remaining rules describe the shape of a value, so blanks are already valid.
    if (!value) continue;

    if (column.account && !ACCOUNT_PATTERN.test(value)) {
      return `${column.key} must be 9–18 digits with no spaces or letters`;
    }

    if (column.numeric) {
      // Comma stripping matches toUploadPayload, so what validates is what is sent.
      const numeric = Number(value.replace(/,/g, ""));
      if (!Number.isFinite(numeric)) {
        return `${column.key} must be a number`;
      }
      if (numeric <= 0) {
        return `${column.key} must be greater than 0`;
      }
    }

    if (column.date && !moment(value, "YYYY-MM-DD", true).isValid()) {
      return `${column.key} must be a real date in YYYY-MM-DD format`;
    }

    if (column.month && !moment(value, "YYYY-MM", true).isValid()) {
      return `${column.key} must be in YYYY-MM format`;
    }
  }
  return null;
}

/** Contract columns that the uploaded file's header row is missing. */
export function findMissingHeaders(fileHeaders = [], columns = []) {
  const present = new Set(fileHeaders.map((h) => String(h ?? "").trim()));
  return columns.map((c) => c.key).filter((key) => !present.has(key));
}

/**
 * Maps parsed sheet rows to the bulk-upload request body.
 *
 * The endpoint takes a plain JSON array of rows, but the types matter: `amount`
 * must be a number (5000, not "5000"), and blank optional fields must be null
 * rather than "" — SD in particular sends payment_month: null. Parsed sheet
 * cells are always strings, so both conversions happen here.
 *
 * Only contract columns are forwarded; extra columns in the user's file are
 * dropped rather than passed through to the API.
 */
export function toUploadPayload(rows = [], columns = []) {
  return rows.map((row) => {
    const record = {};
    columns.forEach((column) => {
      const raw = row?.[column.key];
      const value = raw === null || raw === undefined ? "" : String(raw).trim();

      if (column.numeric) {
        const numeric = Number(value.replace(/,/g, ""));
        record[column.key] = Number.isFinite(numeric) ? numeric : null;
        return;
      }
      record[column.key] = value === "" ? null : value;
    });
    return record;
  });
}
